import { useCallback, useState } from 'react';
import { useReactiveVar } from '@apollo/client';

import {
  CourseTariffPricesQuery,
  useCourseCardNameQuery,
  useCourseTariffPricesQuery,
  useRemoveCourseTariffPriceMutation,
  useSaveCourseTariffPriceMutation,
} from 'core/graphql';
import { userStateVar } from 'core/state/userState';
import { UserRole } from 'types';

export type ICoursePlanPrice = CourseTariffPricesQuery['coursePlanPrices'][number];

export interface ICoursePlanPriceError {
  field: string;
  code: string;
  message: string;
}

export interface ICoursePlanPriceResult {
  ok: boolean;
  errors: ICoursePlanPriceError[];
}

/**
 * #132 — зеркало ролевых списков бэкенда для цен курса по тарифам.
 * Чтение: Admin, SalesManager, AccountManager, FinanceManager.
 * Запись: те же без FinanceManager.
 */
export const COURSE_PLAN_PRICE_WRITE_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.SALESMANAGER,
  UserRole.ACCOUNTANT,
];

export const COURSE_PLAN_PRICE_READ_ROLES: UserRole[] = [
  ...COURSE_PLAN_PRICE_WRITE_ROLES,
  UserRole.FINANCIER,
];

export const useCoursePlanPricePermissions = () => {
  const userState = useReactiveVar(userStateVar);
  const roles = (userState.me?.roles ?? []) as UserRole[];
  return {
    canRead: roles.some((role) => COURSE_PLAN_PRICE_READ_ROLES.includes(role)),
    canWrite: roles.some((role) =>
      COURSE_PLAN_PRICE_WRITE_ROLES.includes(role),
    ),
  };
};

type RawErrors =
  | Array<{
      field?: string | null;
      code?: string | null;
      message?: string | null;
    } | null>
  | null
  | undefined;

const toErrors = (raw: RawErrors): ICoursePlanPriceError[] =>
  (raw ?? [])
    .filter((error): error is NonNullable<typeof error> => Boolean(error))
    .map((error) => ({
      field: error.field ?? '',
      code: error.code ?? '',
      message: error.message ?? '',
    }));

const NETWORK_ERROR: ICoursePlanPriceResult = {
  ok: false,
  errors: [{ field: '', code: 'NETWORK', message: '' }],
};

const EMPTY: ICoursePlanPrice[] = [];

/**
 * #132 — цены курса по тарифам: список, сохранить (upsert), удалить.
 *
 * Перечитываем список собственным `refetch` этого запроса, а не
 * `refetchQueries` по имени: по имени бьётся и пропущенный (`skip`) запрос.
 */
export const useCoursePlanPrices = (courseId: string) => {
  const { canRead, canWrite } = useCoursePlanPricePermissions();
  const { data, loading, error, refetch } = useCourseTariffPricesQuery({
    variables: { courseId },
    skip: !canRead || !courseId,
    fetchPolicy: 'cache-and-network',
  });
  const [saveMutation] = useSaveCourseTariffPriceMutation();
  const [removeMutation] = useRemoveCourseTariffPriceMutation();
  const [saving, setSaving] = useState(false);

  const run = useCallback(
    async (
      action: () => Promise<{ success: boolean; raw: RawErrors }>,
    ): Promise<ICoursePlanPriceResult> => {
      setSaving(true);
      try {
        const { success, raw } = await action();
        if (success) await refetch();
        return { ok: success, errors: toErrors(raw) };
      } catch {
        return NETWORK_ERROR;
      } finally {
        setSaving(false);
      }
    },
    [refetch],
  );

  const savePrice = useCallback(
    (planId: string, currency: string, amount: string) =>
      run(async () => {
        const result = await saveMutation({
          variables: { courseId, planId, currency, amount },
        });
        const payload = result.data?.setCoursePlanPrice;
        return {
          success: Boolean(payload?.success),
          raw: payload?.customErrors,
        };
      }),
    [courseId, run, saveMutation],
  );

  const removePrice = useCallback(
    (id: string) =>
      run(async () => {
        const result = await removeMutation({ variables: { id } });
        const payload = result.data?.deleteCoursePlanPrice;
        return {
          success: Boolean(payload?.success),
          raw: payload?.customErrors,
        };
      }),
    [run, removeMutation],
  );

  // Перечитать после ошибки загрузки: отказ остаётся в `error`.
  const reload = useCallback(() => {
    refetch().catch(() => undefined);
  }, [refetch]);

  return {
    prices: data?.coursePlanPrices ?? EMPTY,
    loading,
    error,
    reload,
    saving,
    canRead,
    canWrite,
    savePrice,
    removePrice,
  };
};

/** #132 — имя курса для заголовка карточки (все роли входа в ERP). */
export const useCourseCardName = (courseId: string): string => {
  const { data } = useCourseCardNameQuery({
    variables: { id: courseId },
    skip: !courseId,
    fetchPolicy: 'cache-first',
  });
  return data?.course?.name ?? '';
};
